import { useMutation } from "@apollo/client/react";
import { useCallback, useMemo, useState } from "react";
import {
  HourglassIcon,
  MailIcon,
  Trash2Icon,
  UserIcon,
  UserPlusIcon,
  UsersIcon,
} from "lucide-react";

import type {
  EntityFragment,
  EntityMembersQuery,
} from "@/__generated__/graphql";
import { SettingsViewCard } from "@/app/app/_/views/SettingsView/SettingsViewLayout";
import { Icon } from "@/components/atoms/Icon";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { useEntityRoles } from "@/components/organisms/Roles/Roles.hooks";
import { describePermissions } from "@/components/organisms/Roles/Roles.tools";
import { RolesSelect } from "@/components/organisms/Roles/RolesSelect";
import { Mutations, Queries } from "@/constants";
import { EnumColors } from "@/constants/values";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { useScope } from "@/hooks/useScope";
import { State } from "@/state";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

type EntityMembers = Extract<
  NonNullable<EntityMembersQuery["node"]>,
  { __typename?: "EntityObject" }
>;
type EntityMember = EntityMembers["accounts"][number];

/** The member drawn while the members load: a typical one, each piece wrapped. */
const PLACEHOLDER_MEMBER = {
  __typename: "AccountObject",
  id: "placeholder",
  identifier: "placeholder",
  memberDisplayName: "Member name",
  memberEmailAddress: "member@business.example.com",
  isOwner: false,
  isExternal: false,
  externalGrantIdentifier: null,
  permissions: [],
  role: {
    __typename: "RoleObject",
    id: "placeholder-role",
    identifier: "placeholder-role",
    name: "Bookkeeper",
  },
} as unknown as EntityMember;

type EntityMembersPanelProps = {
  entity: EntityFragment;
};

export function EntityMembersPanel({ entity }: EntityMembersPanelProps) {
  const { activeEntity, accounts } = useScope();
  const [email, setEmail] = useState("");
  const [isOwnerInvite, setIsOwnerInvite] = useState(false);
  // The role a member invited holds: picked, never guessed for them.
  const [inviteRoleIdentifier, setInviteRoleIdentifier] = useState<
    string | null
  >(null);
  const { roles } = useEntityRoles(entity.id);

  const activeAccount = accounts?.find(
    (account) => account?.entity?.identifier === entity.identifier,
  );
  const isOwner = Boolean(activeAccount?.isOwner);
  const isUserEntity = entity.isUserEntity;

  // Member rows are this panel's payload — not on the base Entity fragment.
  const { data: membersData, loading: membersLoading } = useBackendQuery(
    Queries.ENTITY_MEMBERS_QUERY,
    {
      variables: { id: entity.id },
      fetchPolicy: "cache-and-network",
    },
  );
  const members: EntityMember[] = useMemo(
    () =>
      membersData?.node?.__typename === "EntityObject"
        ? membersData.node.accounts
        : [],
    [membersData],
  );

  const [inviteUser, { loading: inviteLoading }] = useMutation(
    Mutations.INVITE_USER_TO_ENTITY,
    { refetchQueries: ["User", "PendingInvitations", "EntityMembers"] },
  );
  const [resendInvitation, { loading: resendLoading }] = useMutation(
    Mutations.RESEND_ENTITY_INVITATION,
    { refetchQueries: ["User"] },
  );
  const [revokeInvitation, { loading: revokeLoading }] = useMutation(
    Mutations.REVOKE_ENTITY_INVITATION,
    { refetchQueries: ["User"] },
  );
  const [removeUser, { loading: removeLoading }] = useMutation(
    Mutations.REMOVE_USER_FROM_ENTITY,
    { refetchQueries: ["User", "EntityMembers"] },
  );
  const [updateMember, { loading: updateLoading }] = useMutation(
    Mutations.UPDATE_ENTITY_MEMBER,
    { refetchQueries: ["User", "EntityMembers"] },
  );

  const handleInvite = useCallback(async () => {
    const trimmed = email.trim();
    if (!trimmed) return;

    await inviteUser({
      variables: {
        input: {
          entityIdentifier: entity.identifier,
          emailAddress: trimmed,
          isOwner: isOwnerInvite,
          ...(isOwnerInvite ? {} : { roleIdentifier: inviteRoleIdentifier }),
        },
      },
    });
    setEmail("");
    setInviteRoleIdentifier(null);
    State.setState({
      snackbarMessage: "Invitation sent.",
    });
  }, [
    email,
    entity.identifier,
    inviteUser,
    inviteRoleIdentifier,
    isOwnerInvite,
  ]);

  const activeOwnerCount = useMemo(
    () => members.filter((member) => member.isOwner).length,
    [members],
  );

  if (isUserEntity || !isOwner) {
    return (
      <div className="flex flex-col gap-3 p-4">
        <SettingsViewCard
          icon={UsersIcon}
          title="Members"
          description={
            isUserEntity
              ? "Your personal books are yours alone: no one else can join them."
              : `Only owners of ${entity.name ?? "this business"} manage its members.`
          }
        />
      </div>
    );
  }

  const invitedMembers = entity.invitedMembers ?? [];
  const isLoadingMembers = membersLoading && members.length === 0;

  return (
    <div className="flex flex-col gap-3 p-4">
      <SettingsViewCard
        icon={UserPlusIcon}
        title="Invite a member"
        description={`They get an email to join ${entity.name ?? "this business"}.`}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void handleInvite();
          }}
          className="flex items-center gap-3 pl-3 pr-3 min-h-11 [border-top-width:1px] [border-top-style:solid] border-border pt-2 pb-2 flex-wrap"
        >
          <div className="flex text-muted-foreground/80">
            <Icon ReactIcon={MailIcon} size="md" />
          </div>
          <Input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="colleague@company.com"
            aria-label="Email address"
            className="h-8 w-auto"
          />
          <div className="flex items-center gap-2">
            <Switch
              checked={isOwnerInvite}
              onCheckedChange={(checked) => setIsOwnerInvite(checked === true)}
            />
            <Typography level="body-sm" className="text-muted-foreground">
              As owner
            </Typography>
          </div>
          {!isOwnerInvite && (
            <RolesSelect
              roles={roles}
              value={inviteRoleIdentifier}
              onChange={(role) => setInviteRoleIdentifier(role.identifier)}
              data-testid="entity-members-invite-role"
            />
          )}
          <Button
            variant="ghost"
            size="sm"
            type="submit"
            disabled={
              inviteLoading ||
              !email.trim() ||
              (!isOwnerInvite && !inviteRoleIdentifier)
            }
          >
            {inviteLoading && <Spinner />}Send invitation
          </Button>
        </form>
      </SettingsViewCard>

      <SettingsViewCard
        icon={UsersIcon}
        title="Members"
        description={
          isLoadingMembers
            ? "Everyone who works in these books."
            : `${members.length} active${
                activeEntity?.identifier === entity.identifier
                  ? ", each billed as a seat"
                  : ""
              }.`
        }
        data-testid="entity-active-members-list"
      >
        {(isLoadingMembers ? [PLACEHOLDER_MEMBER] : members).map((member) => (
          <div
            key={member.id}
            className="flex items-center gap-3 pl-3 pr-3 min-h-11 [border-top-width:1px] [border-top-style:solid] border-border"
          >
            <Skeleton
              loading={isLoadingMembers}
              variant="rectangular"
              className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
            >
              <div className="flex text-muted-foreground/80">
                <Icon ReactIcon={UserIcon} size="md" />
              </div>
            </Skeleton>
            <div className="flex-1 min-w-0 pt-1.5 pb-1.5">
              <Typography level="body-md" className="truncate">
                <Skeleton loading={isLoadingMembers} variant="inline">
                  {member.memberDisplayName ?? member.memberEmailAddress ?? "—"}
                </Skeleton>
              </Typography>
              <Typography
                level="body-sm"
                className="truncate text-muted-foreground/80"
              >
                <Skeleton loading={isLoadingMembers} variant="inline">
                  {member.memberEmailAddress}
                </Skeleton>
              </Typography>
            </div>
            <Skeleton
              loading={isLoadingMembers}
              variant="rectangular"
              className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
            >
              {/* Someone working here through a firm is a member, never an owner: what they may do is the firm's
                  grant's, changed where the books are shared. */}
              {member.isExternal ? (
                // A firm's name as it is written - an enum chip would recase it.
                <Typography
                  level="body-sm"
                  className="truncate text-muted-foreground"
                >
                  {/* What they may do here: the firm's grant and their hand-on, both. */}
                  {`Through ${member.firmName} · ${describePermissions(member.permissions)}`}
                </Typography>
              ) : member.isOwner ? (
                <SimpleEnum label="Owner" enumColor={EnumColors.BLUE} />
              ) : (
                <RolesSelect
                  roles={roles}
                  value={member.role?.identifier ?? null}
                  onChange={(role) =>
                    void updateMember({
                      variables: {
                        input: {
                          accountIdentifier: member.identifier,
                          roleIdentifier: role.identifier,
                        },
                      },
                    })
                  }
                  data-testid={`entity-member-${member.identifier}-role`}
                />
              )}
            </Skeleton>
            <Skeleton
              loading={isLoadingMembers}
              variant="rectangular"
              className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
            >
              <MemberActions
                isOwner={member.isOwner}
                isExternal={member.isExternal}
                isLastOwner={member.isOwner && activeOwnerCount <= 1}
                updateLoading={updateLoading}
                removeLoading={removeLoading}
                onUpdateRole={(nextIsOwner) =>
                  void updateMember({
                    variables: {
                      input: {
                        accountIdentifier: member.identifier,
                        isOwner: nextIsOwner,
                      },
                    },
                  })
                }
                onRemove={() =>
                  void removeUser({
                    variables: {
                      input: {
                        accountIdentifier: member.identifier,
                      },
                    },
                  })
                }
                testId={`entity-member-${member.identifier}`}
              />
            </Skeleton>
          </div>
        ))}
      </SettingsViewCard>

      {invitedMembers.length > 0 && (
        <SettingsViewCard
          icon={HourglassIcon}
          title="Pending invitations"
          description="Sent, and not yet accepted."
          data-testid="entity-pending-invitations-list"
        >
          {invitedMembers.map((member) => (
            <div
              key={member.id}
              className="flex items-center gap-3 pl-3 pr-3 min-h-11 [border-top-width:1px] [border-top-style:solid] border-border"
            >
              <div className="flex text-muted-foreground/80">
                <Icon ReactIcon={MailIcon} size="md" />
              </div>
              <div className="flex-1 min-w-0 pt-1.5 pb-1.5">
                <Typography level="body-md" className="truncate">
                  {member.memberEmailAddress ?? "—"}
                </Typography>
                {member.invitedAt && (
                  <Typography
                    level="body-sm"
                    className="truncate text-muted-foreground/80"
                  >
                    Invited {new Date(member.invitedAt).toLocaleDateString()}
                  </Typography>
                )}
              </div>
              <InvitationActions
                resendLoading={resendLoading}
                revokeLoading={revokeLoading}
                onResend={() =>
                  void resendInvitation({
                    variables: {
                      input: {
                        accountIdentifier: member.identifier,
                      },
                    },
                  })
                }
                onRevoke={() =>
                  void revokeInvitation({
                    variables: {
                      input: {
                        accountIdentifier: member.identifier,
                      },
                    },
                  })
                }
                testId={`entity-invitation-${member.identifier}`}
              />
            </div>
          ))}
        </SettingsViewCard>
      )}
    </div>
  );
}

function MemberActions({
  isOwner,
  isExternal,
  isLastOwner,
  updateLoading,
  removeLoading,
  onUpdateRole,
  onRemove,
  testId,
}: {
  isOwner: boolean;
  /** Held through a member's grant: removed from this business, never made an owner. */
  isExternal: boolean;
  isLastOwner: boolean;
  updateLoading: boolean;
  removeLoading: boolean;
  onUpdateRole: (isOwner: boolean) => void;
  onRemove: () => void;
  testId: string;
}) {
  const canDemote = isOwner && !isLastOwner;
  const canRemove = !isLastOwner;

  return (
    <div
      data-testid={testId}
      className="flex items-center justify-end gap-1"
      onClick={(event) => event.stopPropagation()}
    >
      {!isExternal && (
        <Button
          variant="ghost"
          size="sm"
          disabled={updateLoading || (isOwner ? !canDemote : false)}
          onClick={() => onUpdateRole(!isOwner)}
        >
          {updateLoading && <Spinner />}
          {isOwner ? "Make member" : "Make owner"}
        </Button>
      )}
      <Button
        variant="ghost"
        size="icon-xs"
        disabled={removeLoading || !canRemove}
        onClick={onRemove}
        className="text-destructive hover:text-destructive"
      >
        {removeLoading ? <Spinner /> : <Trash2Icon size="1em" />}
      </Button>
    </div>
  );
}

function InvitationActions({
  resendLoading,
  revokeLoading,
  onResend,
  onRevoke,
  testId,
}: {
  resendLoading: boolean;
  revokeLoading: boolean;
  onResend: () => void;
  onRevoke: () => void;
  testId: string;
}) {
  return (
    <div
      data-testid={testId}
      className="flex items-center justify-end gap-1"
      onClick={(event) => event.stopPropagation()}
    >
      <Button
        variant="ghost"
        size="sm"
        onClick={onResend}
        disabled={resendLoading}
      >
        {resendLoading && <Spinner />}Resend
      </Button>
      <Button
        variant="ghost"
        size="icon-xs"
        onClick={onRevoke}
        disabled={revokeLoading}
        className="text-destructive hover:text-destructive"
      >
        {revokeLoading ? <Spinner /> : <Trash2Icon size="1em" />}
      </Button>
    </div>
  );
}
